import { useEffect, useMemo, useRef, useState } from "react"
import { Navigate } from "react-router-dom"
import { useDocuments } from "hooks/api/drawings/drawings.hook"
import { usePresignedDownloadUrl } from "hooks/api/downloadFIle/downloadFIle.hook"
import { QuotationViewerModal } from "components/ui/customWebviewer/QuotationViewerModal"
import { QuotationEditorDialogs } from "components/ui/customWebviewer/QuotationEditorDialogs/QuotationEditorDialogs"
import {
	POP_OUT_WINDOW_PATH,
	buildPopOutWindowUrl,
	readPopOutWindowParams,
} from "components/ui/customWebviewer/core/popOutWindow.helpers"
import { removeApiVersion } from "helpers/utils.helper"
import { useSyncCurrentUser } from "hooks/currentUser.hook"
import { useCurrentUserStore } from "store/user.store"
import type { TDrawingStatus } from "types/drawer.type"
import style from "./PageQuotationWindow.module.scss"

const { page } = style

// The quotation editor running in its OWN browser window — #1430.
//
// Opened by `interactions/usePopOutWindow` via `window.open` on this route, which is the only way
// to get a view the user can drag onto a second monitor: the in-window dual view and any floating
// in-app pane are both confined to the opener's window.
//
// This is a full, independent boot of the app — its own document, JS realm and TanStack cache — so
// it resolves everything from the URL rather than being handed state. That independence is the
// point: two Apryse instances in two realms cannot collide the way two in one realm do (#1119).
// The two windows reconcile through the server (helpers/quotationWindowSync.helper.ts).
//
// It renders the SAME `QuotationViewerModal` the main app uses, in editor mode. Reusing it rather
// than building a parallel host is what satisfies "the same header and left and right bars, the
// same functionality" — the pop-out inherits every editor feature (sidebars, hotkeys, context
// menu, batch ops) and can never drift from the primary editor's behaviour. The ONE thing it does
// not inherit is the in-window dual view, which `useSplitScreen` switches off on this route.
export const PageQuotationWindow = () => {
	// Read ONCE, at mount, rather than subscribing to the live search params: the effect below
	// strips the query string immediately, and a subscription would then re-render this page with no
	// ids and unmount the editor it had just mounted. `readPopOutWindowParams` also mirrors them
	// into this window's sessionStorage, which is what makes the stripped URL survive a reload.
	const [params] = useState(() =>
		readPopOutWindowParams(window.location.search, typeof sessionStorage === "undefined" ? null : sessionStorage),
	)

	// Hide the ids the moment they have been read (#1430, requirement 2). A pop-up already has no
	// editable address bar — only Chrome's non-editable origin strip, which no API can remove — so
	// what is left to protect is the URL reachable through right-click → Copy and DevTools. After
	// this the URL carries nothing: a copied link opens the bare route in a context with no
	// sessionStorage of its own, which redirects to /all-files rather than mounting an empty editor.
	//
	// `replaceState`, not a router navigation: this must not add a history entry and must not
	// re-render the tree. The PATH is deliberately left alone so `isPopOutWindowRoute()` — which is
	// what gates the in-window dual view off in here — stays true for the window's whole life.
	const isUrlStrippedRef = useRef(false)
	useEffect(() => {
		if (isUrlStrippedRef.current || !window.location.search) return
		isUrlStrippedRef.current = true
		window.history.replaceState(window.history.state, "", POP_OUT_WINDOW_PATH)
	}, [])

	// The same two-step the opener does (Quotations.tsx): find the "Original" document, then resolve
	// a fresh presigned URL for it. Resolved HERE rather than passed in the URL because presigned
	// links expire — one baked into a long-lived window would silently stop loading.
	const drawingStatus = (params?.drawingStatus ?? "") as TDrawingStatus
	const { data: documentsData } = useDocuments(params?.drawingId ?? 0, params?.processingId ?? null, drawingStatus)
	const originalDocEndpoint = useMemo(() => {
		const originalDoc = documentsData?.documents?.find((doc: { note: string }) => doc.note === "Original")
		return originalDoc ? removeApiVersion(originalDoc.download_endpoint) : null
	}, [documentsData])
	const { data: originalPdfData } = usePresignedDownloadUrl(originalDocEndpoint)

	// This route sits outside the app shell, so Userbar never fills the current-user store here.
	// The viewer is held until the id is known: the document fetch reads it once, and without it
	// core/documentBlobCache skips the cache the main window already filled (#1466). A failed
	// profile request stops blocking — the drawing then just downloads uncached.
	const { isError: isProfileError } = useSyncCurrentUser()
	const currentUserId = useCurrentUserStore((state) => state.currentUser.id)
	const isUserResolved = currentUserId != null || isProfileError

	// No ids in the URL and none stored for this window: somebody's copied link, not a live pop-out.
	// Send them to the file list instead of mounting an editor that has nothing to show.
	if (!params) return <Navigate to="/all-files" replace />

	// Mount the viewer only once there is a document for it.
	//
	// Not just tidiness: the main app always mounts the viewer with the modal CLOSED and opens it
	// later, so its Apryse init never runs during React StrictMode's double-invoke. This page opens
	// straight into editor mode, so mounting unconditionally starts init inside that window — and
	// since `UI.dispose()` does NOT remove the `<apryse-webviewer>` node, the second invoke left a
	// SECOND instance stacked below the first (measured on the PoC: 2 web components, the live
	// scroll view offset ~920px down — a grey band above page 1). `core/webviewerInit`'s stale-node
	// guard is the belt for that; this is the braces, and it also means we never initialize a viewer
	// with nothing to load.
	if (!originalPdfData?.download_url || !isUserResolved) return <main className={page} />

	// Rebuilt from the params, NOT `window.location.href`: this window strips its own query after
	// boot, so the live href is the bare route. It is posted verbatim as `processing_url` on "Share
	// feedback" (see FeedbackPanel), and a bare link would hand the triager something that opens nothing.
	const processingUrl = buildPopOutWindowUrl(window.location.origin, params)

	return (
		<main className={page}>
			<QuotationViewerModal
				mode="editor"
				// The editor's close button closes the WINDOW here — there is nothing to return to. A
				// window that cannot be closed from inside would strand the user on the OS chrome.
				onClose={() => window.close()}
				fileLink={originalPdfData.download_url}
				drawingId={params.drawingId}
				processingId={params.processingId ?? 0}
				processingUrl={processingUrl}
				drawingStatus={drawingStatus}
			/>
			{/* The header's "Quotation configuration" / "Share feedback" open through viewer.store, so
			    this host has to render them just as the Quotations drawer does (#1430 review). */}
			<QuotationEditorDialogs
				drawingId={params.drawingId}
				processingId={params.processingId}
				quotationId={params.quotationId ?? undefined}
				processingUrl={processingUrl}
				drawingStatus={drawingStatus}
			/>
		</main>
	)
}
